'use client'

import { ArrowBigUp, ArrowUpRight, ChevronDown, ExternalLink, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import type { RedditSearchResult } from 'sprites-types/workflow/tools/redditSearch'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'

type RedditPost = RedditSearchResult[number]

interface RedditPostItemProps {
  post: RedditPost
  className?: string
}

export function RedditPostItem({ post, className }: RedditPostItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const createdAt = new Date(post.created_utc * 1000)
  const dateLabel = isNaN(createdAt.getTime())
    ? String(post.created_utc)
    : createdAt.toLocaleString()
  const textPreview =
    post.content && post.content.length > 200 ? post.content.slice(0, 200) + '…' : post.content
  const toggleExpanded = () => setIsExpanded((prev) => !prev)

  // Context actions for individual Reddit posts
  const contextActions: ContextAction[] = [
    {
      id: 'open-post',
      label: 'Open Post',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: () => window.open(post.url, '_blank'),
    },
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <article
        className={`rounded-3xl border border-gray-200/50 bg-white p-4 text-black shadow-sm backdrop-blur-[8px] ${className}`}
      >
        <button
          type="button"
          onClick={toggleExpanded}
          className="w-full text-left"
          aria-expanded={isExpanded}
          aria-controls={`reddit-post-${post.id}-content`}
        >
          <header className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm">
                <span className="truncate font-semibold">r/{post.subreddit}</span>
                <span className="truncate text-gray-600">by {post.author}</span>
              </div>
              <h3 className="mt-1 break-words text-base font-semibold leading-snug">
                {post.title}
              </h3>
              <div className="mt-1 text-xs text-gray-500">{dateLabel}</div>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span
                title="Upvotes"
                aria-label="Upvotes"
                className="inline-flex items-center gap-1 rounded-full border border-gray-200/70 px-2 py-0.5 text-[11px] text-gray-700 hover:bg-rose-50 hover:text-rose-600"
              >
                <ArrowBigUp className="h-3.5 w-3.5" />
                <span className="tabular-nums">{post.score}</span>
              </span>
              <span
                title="Comments"
                aria-label="Comments"
                className="inline-flex items-center gap-1 rounded-full border border-gray-200/70 px-2 py-0.5 text-[11px] text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                <span className="tabular-nums">{post.num_comments}</span>
              </span>
              <ChevronDown
                className={`ml-1 h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </header>

          {!isExpanded && (
            <div className="mt-2 text-sm text-black">
              {textPreview && <div className="truncate">{textPreview}</div>}
              {post.thumbnails?.length ? (
                <div className="mt-1 text-xs text-gray-600">
                  {post.thumbnails.length} image{post.thumbnails.length === 1 ? '' : 's'}
                </div>
              ) : null}
            </div>
          )}
        </button>

        {isExpanded && (
          <div id={`reddit-post-${post.id}-content`} className="mt-3">
            {post.content && (
              <p className="whitespace-pre-wrap break-words text-sm text-black">{post.content}</p>
            )}

            {post.thumbnails?.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {post.thumbnails.map((url, idx) => (
                  <a
                    key={`${post.id}-thumb-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={url} alt="thumbnail" className="h-auto w-full rounded-2xl" />
                  </a>
                ))}
              </div>
            )}

            <div className="mt-3">
              <a
                href={post.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl border border-gray-200/70 px-3 py-1.5 text-blue-700 hover:bg-gray-50"
              >
                <span className="font-medium">Open on Reddit</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-gray-600" aria-hidden="true" />
              </a>
            </div>
          </div>
        )}
      </article>
    </ContextMenuWrapper>
  )
}
